<template>
  <v-layout
    class="layout"
    :class="{
      'watermark-preprod': isPreProd(),
      'watermark-ctma': isCetaitMieuxAvant(),
    }"
  >
    <Header />
    <DesktopSideNav
      @flip-content="flipContent"
      @unflip-content="unflipContent"
    />
    <v-main class="main">
      <div class="fake-background">
        <div class="content" :class="{ flip: shouldFlipContent }">
          <slot />
        </div>
      </div>
    </v-main>
    <MobileBottomNav />

    <v-dialog
      v-model="shouldApproveEULA"
      transition="dialog-bottom-transition"
      max-width="600"
      persistent
    >
      <ApproveEULADialogCard />
    </v-dialog>
    <v-dialog
      v-model="shouldSignVolunteerCharter"
      transition="dialog-bottom-transition"
      max-width="600"
      persistent
    >
      <MustSignVolunteerCharterDialogCard />
    </v-dialog>
  </v-layout>
</template>

<script lang="ts" setup>
import {
  PERMISSION_GRANTED,
  PERMISSION_REVOKED,
  TEAMS_JOINED,
  TEAM_LEFT,
} from "@overbookd/access-manager";
import {
  FESTIVAL_ACTIVITY_APPROVED,
  FESTIVAL_ACTIVITY_READY_TO_REVIEW,
  FESTIVAL_ACTIVITY_REJECTED,
  FESTIVAL_TASK_APPROVED,
  FESTIVAL_TASK_READY_TO_REVIEW,
  FESTIVAL_TASK_REJECTED,
  FESTIVAL_TASK_IGNORED,
  FESTIVAL_TASK_DO_REVIEW,
  FESTIVAL_TASK_READY_TO_ASSIGN,
} from "@overbookd/domain-events";
import { CANDIDATE_ENROLLED } from "@overbookd/registration";
import { MUST_SIGN_VOLUNTEER_CHARTER } from "@overbookd/permission";
import { useFavicon } from "@vueuse/core";
import { useTheme } from "vuetify";
import { useLiveNotification } from "~/composable/useLiveNotification";
import Header from "~/layouts/header/Header.vue";
import DesktopSideNav from "~/layouts/navigation/DesktopSideNav.vue";
import MobileBottomNav from "~/layouts/navigation/MobileBottomNav.vue";
import {
  isContentFlipped,
  saveContentFlipped,
  saveContentUnflipped,
} from "~/utils/easter-egg/flip-content";
import { isCetaitMieuxAvant, isPreProd } from "~/utils/navigation/url.constant";
import { pickDefaultTheme } from "~/utils/vuetify/theme/theme.utils";
import type { FestivalActivity, FestivalTask } from "@overbookd/festival-event";

const theme = useTheme();
const { listen, stopListening } = useLiveNotification();
const myStore = useMyStore();
const { fetchMyRefusedActivities, fetchMyRefusedTasks } =
  useNavigationBadgeStore();
const faStore = useFestivalActivityStore();
const ftStore = useFestivalTaskStore();

const favicon = useFavicon();
if (isPreProd()) favicon.value = "/favicon-preprod.ico";
if (isCetaitMieuxAvant()) favicon.value = "/favicon-ctma.ico";

const isMyFestivalActivity = (activity: FestivalActivity): boolean =>
  activity.inCharge.adherent.id === myStore.loggedUser?.id;
const isMyFestivalTask = (task: FestivalTask): boolean =>
  task.general.administrator.id === myStore.loggedUser?.id;

onMounted(() => {
  theme.change(pickDefaultTheme());
  listen(PERMISSION_GRANTED, () => myStore.fetchMyInformations());
  listen(PERMISSION_REVOKED, () => myStore.fetchMyInformations());
  listen(TEAMS_JOINED, () => myStore.fetchMyInformations());
  listen(TEAM_LEFT, () => myStore.fetchMyInformations());
  listen(CANDIDATE_ENROLLED, () => myStore.fetchMyInformations());
  listen(FESTIVAL_ACTIVITY_REJECTED, ({ data }) => {
    if (!isMyFestivalActivity(data.festivalActivity)) return;
    faStore.updateMyPreview(data.festivalActivity);
    fetchMyRefusedActivities();
  });
  listen(FESTIVAL_ACTIVITY_READY_TO_REVIEW, ({ data }) => {
    if (!isMyFestivalActivity(data.festivalActivity)) return;
    faStore.updateMyPreview(data.festivalActivity);
    fetchMyRefusedActivities();
  });
  listen(FESTIVAL_ACTIVITY_APPROVED, ({ data }) => {
    if (!isMyFestivalActivity(data.festivalActivity)) return;
    faStore.updateMyPreview(data.festivalActivity);
    fetchMyRefusedActivities();
  });
  listen(FESTIVAL_TASK_REJECTED, ({ data }) => {
    if (!isMyFestivalTask(data.festivalTask)) return;
    ftStore.updateMyPreview(data.festivalTask);
    fetchMyRefusedTasks();
  });
  listen(FESTIVAL_TASK_READY_TO_REVIEW, ({ data }) => {
    if (!isMyFestivalTask(data.festivalTask)) return;
    ftStore.updateMyPreview(data.festivalTask);
    fetchMyRefusedTasks();
  });
  listen(FESTIVAL_TASK_APPROVED, ({ data }) => {
    if (!isMyFestivalTask(data.festivalTask)) return;
    ftStore.updateMyPreview(data.festivalTask);
    fetchMyRefusedTasks();
  });
  listen(FESTIVAL_TASK_IGNORED, ({ data }) => {
    if (!isMyFestivalTask(data.festivalTask)) return;
    ftStore.updateMyPreview(data.festivalTask);
    fetchMyRefusedTasks();
  });
  listen(FESTIVAL_TASK_DO_REVIEW, ({ data }) => {
    if (!isMyFestivalTask(data.festivalTask)) return;
    ftStore.updateMyPreview(data.festivalTask);
    fetchMyRefusedTasks();
  });
  listen(FESTIVAL_TASK_READY_TO_ASSIGN, ({ data }) => {
    if (!isMyFestivalTask(data.festivalTask)) return;
    ftStore.updateMyPreview(data.festivalTask);
  });
});

onUnmounted(() => {
  stopListening();
});

const shouldApproveEULA = computed<boolean>(
  () => myStore.loggedUser?.hasApprovedEULA === false,
);
const shouldSignVolunteerCharter = computed<boolean>(
  () =>
    myStore.can(MUST_SIGN_VOLUNTEER_CHARTER) &&
    myStore.loggedUser?.hasSignedVolunteerCharter === false,
);

const shouldFlipContent = ref<boolean>(isContentFlipped());
const flipContent = () => {
  shouldFlipContent.value = true;
  saveContentFlipped();
};
const unflipContent = () => {
  shouldFlipContent.value = false;
  saveContentUnflipped();
};
</script>

<style lang="scss" scoped>
@use "~/assets/watermark.scss" as *;

$surface-color: rgb(var(--v-theme-surface));
$background-color: rgb(var(--v-theme-background));

.layout {
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  @media screen and (max-width: $mobile-max-width) {
    background: $background-color;
  }
}

.main {
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
  @media screen and (max-width: $mobile-max-width) {
    padding-bottom: $bottom-nav-height;
  }

  .fake-background {
    background: $surface-color;
    height: 100%;
    width: 100%;
  }

  .content {
    width: 100%;
    height: 100%;
    padding: $desktop-content-vertical-padding
      $desktop-content-horizontal-padding;
    background: $background-color;
    overflow-y: auto;
    border-top-left-radius: $main-page-border-radius;
    border-top: 1px solid $background-color;
    border-left: 1px solid $background-color;

    @media screen and (max-width: $mobile-max-width) {
      padding: $mobile-content-padding;
      border-top-right-radius: $main-page-border-radius;
      border-left: none;
    }
  }

  .flip {
    transform: rotate(180deg);
  }
}
</style>
